import { Link } from 'react-router-dom';
import { RISK_COLORS, type RiskLevel } from '../../types';
import { BAND_SHARE_LABEL, SHARE_CAVEAT, type BandKind } from '../../lib/riskText';

// Legacy cutoffs mirror the backend DEFAULT_THRESHOLDS (moderate 0.05 / high 0.10 /
// extreme 0.20), rescaled 2026-06-04. Wildfire is a rare event, so a calibrated
// 5% probability is already "moderate" (~10x the background base rate).
const RISK_LEVELS: { level: RiskLevel; label: string; labelPt: string; threshold: string }[] = [
  { level: 'Low', label: 'Low', labelPt: 'Baixo', threshold: '< 5%' },
  { level: 'Moderate', label: 'Moderate', labelPt: 'Moderado', threshold: '5-10%' },
  { level: 'High', label: 'High', labelPt: 'Alto', threshold: '10-20%' },
  { level: 'Extreme', label: 'Extreme', labelPt: 'Extremo', threshold: '≥ 20%' },
];

// Share bands (decisions D3 and D7): fixed shares of the territory per day,
// assigned by rank. Highest band first, since the reader asks "where is the top".
const SHARE_LEVELS: { level: RiskLevel; labelPt: string; threshold: string }[] = [
  { level: 'Extreme', labelPt: 'Extremo', threshold: BAND_SHARE_LABEL.Extreme },
  { level: 'High', labelPt: 'Alto', threshold: BAND_SHARE_LABEL.High },
  { level: 'Moderate', labelPt: 'Moderado', threshold: BAND_SHARE_LABEL.Moderate },
  { level: 'Low', labelPt: 'Baixo', threshold: BAND_SHARE_LABEL.Low },
];

// "Anomalia" = how unusual today is for this place and time of year, not the
// absolute risk. Labels stay plain (no percentiles) for non-technical readers.
const ANOMALY_LEVELS: { level: RiskLevel; labelPt: string; threshold: string }[] = [
  { level: 'Low', labelPt: 'Típico', threshold: 'normal p/ época' },
  { level: 'Moderate', labelPt: 'Elevado', threshold: 'acima do normal' },
  { level: 'High', labelPt: 'Notável', threshold: 'bastante acima' },
  { level: 'Extreme', labelPt: 'Anomalia', threshold: 'top 5%' },
];

interface RiskLegendProps {
  visibleLevels?: Set<RiskLevel>;
  onToggle?: (level: RiskLevel) => void;
  viewMode?: 'risk' | 'anomaly';
  dark?: boolean;
  /** Which kind of band the served map carries; defaults to the legacy cutoffs. */
  bandKind?: BandKind;
  /** "hoje" or "amanhã": the day the map is for (share mode heading). */
  dayLabel?: string;
}

export default function RiskLegend({
  visibleLevels,
  onToggle,
  viewMode = 'risk',
  dark = false,
  bandKind = 'probability',
  dayLabel = 'hoje',
}: RiskLegendProps) {
  const interactive = !!onToggle;
  const share = bandKind === 'share';
  const items = viewMode === 'anomaly' ? ANOMALY_LEVELS : share ? SHARE_LEVELS : RISK_LEVELS;
  const heading = viewMode === 'anomaly'
    ? 'Anomalia Sazonal'
    : share
      ? `Risco relativo de ignição ${dayLabel}`
      : 'Nível de Risco';

  return (
    <div className={`rounded-xl p-4 ${dark ? 'bg-ink-900/85 backdrop-blur border border-ink-700 shadow-2xl' : 'bg-white shadow-warm'}`}>
      <h3 className={`font-semibold mb-3 ${dark ? 'font-display text-mist' : 'font-serif text-tutela-forest'}`}>
        {heading}
      </h3>
      <div className="space-y-2">
        {items.map(({ level, labelPt, threshold }) => {
          const visible = !visibleLevels || visibleLevels.has(level);
          const rowClasses = [
            'flex items-center gap-3 select-none',
            interactive ? `cursor-pointer rounded-md px-1 py-0.5 -mx-1 transition-colors ${dark ? 'hover:bg-ink-800' : 'hover:bg-gray-50'}` : '',
            interactive && !visible ? 'opacity-40' : '',
          ].filter(Boolean).join(' ');

          return (
            <div
              key={level}
              className={rowClasses}
              onClick={interactive ? () => onToggle!(level) : undefined}
              role={interactive ? 'button' : undefined}
              tabIndex={interactive ? 0 : undefined}
              onKeyDown={interactive ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle!(level);
                }
              } : undefined}
              title={interactive ? (visible ? `Esconder ${labelPt}` : `Mostrar ${labelPt}`) : undefined}
              aria-pressed={interactive ? visible : undefined}
            >
              <div
                className="w-5 h-5 rounded-full border-2 border-white shadow-sm flex-shrink-0"
                style={{
                  backgroundColor: RISK_COLORS[level],
                  filter: interactive && !visible ? 'grayscale(1)' : 'none',
                }}
              />
              <span
                className={`text-sm font-medium ${dark ? 'text-mist' : 'text-tutela-charcoal'}`}
                style={{ textDecoration: interactive && !visible ? 'line-through' : 'none' }}
              >
                {labelPt}
              </span>
              <span className={`text-xs ml-auto text-right ${dark ? 'font-mono text-mist-muted' : 'text-tutela-slate'}`}>{threshold}</span>
            </div>
          );
        })}
      </div>
      {viewMode === 'risk' && (
        <p className={`mt-3 text-[10px] leading-snug ${dark ? 'text-mist-faint' : 'text-tutela-slate'}`}>
          {share ? SHARE_CAVEAT : 'Níveis relativos, não frequências observadas.'}{' '}
          <Link
            to="/ignis/validacao"
            className={`underline-offset-2 hover:underline ${dark ? 'text-ember/80' : 'text-tutela-forest'}`}
          >
            Ver Validação
          </Link>
        </p>
      )}
    </div>
  );
}
